import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sunrise, Calendar, AlertTriangle, TrendingUp, Users, CheckSquare, Bell, Printer, RefreshCw, Phone, Target } from 'lucide-react';
import { theme, cardStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  MORNING HUDDLE — start-of-day dashboard + printable briefing.
// ═══════════════════════════════════════════════════════════════════

const fmtINR = (n) => '₹' + (n || 0).toLocaleString('en-IN');

const HuddlePage = ({ API_BASE, currentUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_BASE}/huddle/today`); setData(r.data); }
    catch {}
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const printHuddle = () => {
    if (!data) return;
    const w = window.open('', '_blank', 'width=900,height=700');
    const appts = data.appointments.list.map(a => `
      <tr>
        <td>${a.time || ''}</td>
        <td>${a.patientName || ''} ${a.risky ? '<span style="color:#dc2626;font-weight:700">⚠️</span>' : ''}</td>
        <td>${a.patientPhone || ''}</td>
        <td>${a.treatment || a.notes || ''}</td>
        <td>${a.doctor || ''}</td>
      </tr>`).join('');
    const risks = data.highRisk.list.map(r => `<li><strong>${r.patientName}</strong> — ${r.riskReasons.join(', ')}</li>`).join('');
    const alerts = data.alerts.list.map(a => `<li><strong>[${a.severity.toUpperCase()}]</strong> ${a.title} — <em>${a.message}</em></li>`).join('');
    const tasks = data.tasks.list.map(t => `<li>[${t.priority}] ${t.title}${t.assignedToName ? ' → ' + t.assignedToName : ''}</li>`).join('');

    w.document.write(`<!doctype html><html><head><title>Morning Huddle — ${data.date}</title>
    <style>
      body{font-family:'Georgia',serif;max-width:820px;margin:20px auto;padding:0 24px;color:#0f172a}
      h1{font-size:24px;color:#0f172a;border-bottom:3px solid ${theme.primary};padding-bottom:8px;margin:0 0 6px}
      h2{font-size:14pt;color:${theme.primaryDark};margin:18px 0 6px;text-transform:uppercase;letter-spacing:1px;border-bottom:1px solid #cbd5e1;padding-bottom:3px}
      .sub{color:#64748b;font-size:12pt;margin-bottom:14px}
      .kpi{display:flex;gap:18px;margin:14px 0;flex-wrap:wrap}
      .kpi-box{flex:1;min-width:130px;padding:14px;background:#f1f5f9;border-radius:8px;text-align:center}
      .kpi-box .n{font-size:22pt;font-weight:700;color:${theme.primaryDark};margin:0}
      .kpi-box .l{font-size:9pt;color:#64748b;text-transform:uppercase;letter-spacing:1px;margin-top:3px}
      table{width:100%;border-collapse:collapse;font-size:10.5pt;margin:8px 0}
      th,td{border:1px solid #cbd5e1;padding:5px 8px;text-align:left}
      th{background:#f8fafc;font-weight:700;font-size:9.5pt}
      ul{padding-left:22px;margin:4px 0}
      li{margin-bottom:3px;font-size:11pt}
      .footer{margin-top:24px;padding-top:10px;border-top:1px solid #e5e7eb;font-size:9pt;color:#94a3b8;text-align:center}
      @page{size:A4;margin:15mm}
    </style></head><body>
      <h1>🌅 Morning Huddle — ${data.date}</h1>
      <div class="sub">${data.greeting}, team · ${new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>

      <div class="kpi">
        <div class="kpi-box"><div class="n">${data.appointments.total}</div><div class="l">Today's Appointments</div></div>
        <div class="kpi-box"><div class="n">${data.highRisk.count}</div><div class="l">High-Risk Patients</div></div>
        <div class="kpi-box"><div class="n">${fmtINR(data.revenue.yesterday)}</div><div class="l">Kal ka Revenue</div></div>
        <div class="kpi-box"><div class="n">${fmtINR(data.revenue.monthToDate)}</div><div class="l">Month to Date</div></div>
      </div>

      <h2>📅 Today's Appointments (${data.appointments.total})</h2>
      ${data.appointments.total === 0 ? '<p><em>Koi appointment nahi.</em></p>' : `
      <table><thead><tr><th>Time</th><th>Patient</th><th>Phone</th><th>Treatment</th><th>Doctor</th></tr></thead>
      <tbody>${appts}</tbody></table>`}

      ${data.highRisk.count > 0 ? `<h2>⚠️ High-Risk Patients (extra care)</h2><ul>${risks}</ul>` : ''}

      ${data.alerts.count > 0 ? `<h2>🚨 Alerts (${data.alerts.count})</h2><ul>${alerts}</ul>` : ''}

      ${data.tasks.count > 0 ? `<h2>📋 Pending Tasks (${data.tasks.count})</h2><ul>${tasks}</ul>` : ''}

      <h2>💬 Talking Points</h2>
      <ul>
        <li>Kal ka revenue kya tha? Aaj ka target?</li>
        <li>High-risk patients ka extra care — medical history double-check</li>
        <li>Pending treatments (${data.pendingTreatments}): follow-up plan kya hai?</li>
        <li>Kisi patient ki complaint / issue to nahi?</li>
        <li>Inventory / supplies adequate hai?</li>
      </ul>

      <div class="footer">Generated from ORALZY · ${new Date().toLocaleString('en-IN')}</div>
      <script>window.onload=()=>window.print();</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, #f59e0b, #d97706)`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><Sunrise size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> Morning Huddle</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.95, fontSize: 13 }}>
              {data?.greeting || 'Good morning'}, team · {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={load} style={headBtn}><RefreshCw size={14} style={{ marginRight: 4 }} /> Refresh</button>
            <button onClick={printHuddle} style={headBtn}><Printer size={14} style={{ marginRight: 4 }} /> Print Briefing</button>
          </div>
        </div>
      </div>

      {loading || !data ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>Loading…</div>
      ) : (
        <>
          {/* KPI grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 }}>
            <Kpi icon={<Calendar color="#2563eb" />} label="Today's Appts" value={data.appointments.total} />
            <Kpi icon={<AlertTriangle color="#dc2626" />} label="High-Risk" value={data.highRisk.count} urgent={data.highRisk.count > 0} />
            <Kpi icon={<TrendingUp color="#16a34a" />} label="Kal Revenue" value={fmtINR(data.revenue.yesterday)} />
            <Kpi icon={<Target color="#7c3aed" />} label="Month Revenue" value={fmtINR(data.revenue.monthToDate)} />
            <Kpi icon={<Users color="#ea580c" />} label="Pending Tx" value={data.pendingTreatments} />
            <Kpi icon={<Bell color="#be185d" />} label="Alerts" value={data.alerts.count} urgent={data.alerts.count > 0} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            {/* Appointments */}
            <div style={{ ...cardStyle, padding: 14 }}>
              <h3 style={{ margin: '0 0 10px', color: theme.navy, fontSize: 15 }}>📅 Today's Appointments ({data.appointments.total})</h3>
              {data.appointments.total === 0 ? (
                <p style={{ color: theme.textMuted, fontSize: 13 }}>Koi appointment nahi.</p>
              ) : (
                <div style={{ display: 'grid', gap: 4 }}>
                  {data.appointments.list.map(a => (
                    <div key={a._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: a.risky ? '#fee2e2' : theme.bg, borderLeft: a.risky ? '3px solid #dc2626' : '3px solid transparent' }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: theme.primaryDark, minWidth: 50 }}>{a.time || '—'}</span>
                      <span style={{ flex: 1, fontSize: 13 }}>
                        <strong>{a.patientName}</strong>
                        {a.risky && <span style={{ color: '#dc2626', marginLeft: 6 }}>⚠️ {a.riskReasons.join(', ')}</span>}
                        {a.treatment && <span style={{ color: theme.textMuted }}> · {a.treatment}</span>}
                      </span>
                      {a.patientPhone && (
                        <a href={`tel:${a.patientPhone}`} style={{ fontSize: 11, color: theme.primary, textDecoration: 'none' }}>
                          <Phone size={11} style={{ verticalAlign: -1 }} /> {a.patientPhone}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right column: Alerts + Tasks */}
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ ...cardStyle, padding: 14 }}>
                <h3 style={{ margin: '0 0 8px', color: theme.navy, fontSize: 14 }}>🚨 Alerts ({data.alerts.count})</h3>
                {data.alerts.count === 0 ? <p style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ All clear</p> :
                  <div style={{ display: 'grid', gap: 4 }}>
                    {data.alerts.list.slice(0, 5).map(a => (
                      <div key={a._id} style={{ fontSize: 12, padding: '4px 8px', background: theme.bg, borderRadius: 4 }}>
                        <strong style={{ color: a.severity === 'urgent' ? '#dc2626' : '#f59e0b' }}>[{a.severity}]</strong> {a.title}
                      </div>
                    ))}
                  </div>}
              </div>
              <div style={{ ...cardStyle, padding: 14 }}>
                <h3 style={{ margin: '0 0 8px', color: theme.navy, fontSize: 14 }}>📋 Tasks ({data.tasks.count})</h3>
                {data.tasks.count === 0 ? <p style={{ fontSize: 12, color: theme.textMuted }}>No pending tasks</p> :
                  <div style={{ display: 'grid', gap: 4 }}>
                    {data.tasks.list.slice(0, 6).map(t => (
                      <div key={t._id} style={{ fontSize: 12, padding: '4px 8px', background: theme.bg, borderRadius: 4 }}>
                        <strong>[{t.priority}]</strong> {t.title}
                        {t.assignedToName && <span style={{ color: theme.textMuted }}> → {t.assignedToName}</span>}
                      </div>
                    ))}
                  </div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Kpi = ({ icon, label, value, urgent }) => (
  <div style={{ ...cardStyle, padding: 12, display: 'flex', alignItems: 'center', gap: 10, background: urgent ? '#fee2e2' : 'white' }}>
    <div>{icon}</div>
    <div><div style={{ fontSize: 10.5, color: theme.textMuted, textTransform: 'uppercase', fontWeight: 700 }}>{label}</div><div style={{ fontSize: 20, fontWeight: 800, color: theme.navy }}>{value}</div></div>
  </div>
);

const headBtn = { ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' };

export default HuddlePage;
